import { motion } from "framer-motion";
import {
    Sparkles,
    FileText,
    BarChart3,
    CheckCircle2,
    AlertTriangle,
    Settings as Cog,
    Check,
} from "lucide-react";

// Circular Compliance Score Donut
function ComplianceDonut({ value = 92 }) {
    const radius = 38;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (value / 100) * circumference;

    return (
        <div className="relative w-[110px] h-[110px]">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle
                    cx="50"
                    cy="50"
                    r={radius}
                    stroke="rgba(15, 118, 110, 0.15)"
                    strokeWidth="8"
                    fill="none"
                />
                <motion.circle
                    cx="50"
                    cy="50"
                    r={radius}
                    stroke="url(#donutGrad)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    fill="none"
                    initial={{ strokeDashoffset: circumference }}
                    whileInView={{ strokeDashoffset: offset }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                    strokeDasharray={circumference}
                />
                <defs>
                    <linearGradient id="donutGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#14B8A6" />
                        <stop offset="100%" stopColor="#0D9488" />
                    </linearGradient>
                </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-serif text-3xl font-bold text-[#0F766E]">
                    {value}%
                </span>
            </div>
        </div>
    );
}

function ProgressRow({ label, value }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[13px] font-medium text-slate-700">
                    {label}
                </span>
                <span className="font-mono text-xs font-semibold text-slate-600">
                    {value}%
                </span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${value}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    className="h-full rounded-full"
                    style={{
                        background:
                            "linear-gradient(90deg, #14B8A6 0%, #0D9488 100%)",
                    }}
                />
            </div>
        </div>
    );
}

const SIDE_ICONS = [
    { id: "sparkles", icon: Sparkles },
    { id: "files", icon: FileText, active: true },
    { id: "charts", icon: BarChart3 },
    { id: "checks", icon: CheckCircle2 },
    { id: "alerts", icon: AlertTriangle },
    { id: "settings", icon: Cog },
];

export default function DashboardPreview() {
    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="relative"
        >
            {/* glow */}
            <div className="absolute -inset-8 bg-teal-500/20 blur-3xl rounded-[40px] pulse-glow pointer-events-none" />

            <div className="relative rounded-[28px] overflow-hidden bg-white shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] ring-1 ring-teal-500/20">
                <div className="flex">
                    {/* mini sidebar */}
                    <div className="w-[58px] bg-gradient-to-b from-[#0F766E] to-[#0E6760] p-3 flex flex-col items-center gap-3">
                        {SIDE_ICONS.map(({ id, icon: Icon, active }) => (
                            <button
                                key={id}
                                className={`h-9 w-9 rounded-xl flex items-center justify-center transition-all ${
                                    active
                                        ? "bg-white/15 text-white"
                                        : "text-white/60 hover:text-white hover:bg-white/10"
                                }`}
                            >
                                <Icon className="h-4 w-4" />
                            </button>
                        ))}
                    </div>

                    {/* main */}
                    <div className="flex-1 p-5 lg:p-6 bg-slate-50/40">
                        <h3 className="font-serif text-lg font-bold text-slate-900">
                            Tender Analysis Overview
                        </h3>

                        <div className="mt-5 grid grid-cols-3 gap-3">
                            <div className="rounded-2xl bg-white border border-slate-100 p-4 shadow-sm">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                    Compliance Score
                                </p>
                                <div className="mt-2 flex justify-center">
                                    <ComplianceDonut value={92} />
                                </div>
                            </div>
                            <div className="rounded-2xl bg-white border border-slate-100 p-4 shadow-sm">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                    Risk Level
                                </p>
                                <div className="mt-3 flex items-center gap-2">
                                    <div className="h-9 w-9 rounded-full bg-emerald-50 flex items-center justify-center">
                                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <span className="font-serif text-2xl font-bold text-emerald-700">
                                        LOW
                                    </span>
                                </div>
                                <p className="mt-3 text-[11px] text-slate-500">
                                    2 minor flags
                                </p>
                            </div>
                            <div className="rounded-2xl bg-white border border-slate-100 p-4 shadow-sm">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                    Mandatory Checks
                                </p>
                                <p className="mt-3 font-serif text-3xl font-bold text-slate-900">
                                    24<span className="text-slate-400">/26</span>
                                </p>
                                <p className="mt-1 text-[11px] text-slate-500">Completed</p>
                            </div>
                        </div>

                        <div className="mt-4 grid grid-cols-5 gap-3">
                            <div className="col-span-3 rounded-2xl bg-white border border-slate-100 p-4 shadow-sm">
                                <p className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                    Compliance Breakdown
                                </p>
                                <div className="mt-4 space-y-3">
                                    <ProgressRow label="Eligibility Criteria" value={100} />
                                    <ProgressRow label="Technical Requirements" value={90} />
                                    <ProgressRow label="Financial Requirements" value={85} />
                                    <ProgressRow label="Document Checklist" value={92} />
                                </div>
                            </div>
                            <div className="col-span-2 rounded-2xl bg-white border border-slate-100 p-4 shadow-sm">
                                <p className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                    Key Insights
                                </p>
                                <ul className="mt-3 space-y-2.5">
                                    {[
                                        "High match with your capabilities",
                                        "Low risk tender",
                                        "All critical documents identified",
                                    ].map((t) => (
                                        <li
                                            key={t}
                                            className="flex items-start gap-2 text-[12px] text-slate-700 leading-snug"
                                        >
                                            <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                                            {t}
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href="#"
                                    className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-[#0F766E]"
                                >
                                    View Full Report →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
